import langData from "../data/lang"
import config from "../data/siteConfig"

export const getLocale = () => {
  return document.querySelector("body").dataset.lang
}

export const lang = key => {
  const l = langData[getLocale()]
  const val = l[key] ? l[key] : key
  return val.replace ? val.replace(/(?:\r\n|\r|\n)/g, "<br/>") : val
}

export const isTouchDevice = () => {
  return "ontouchstart" in window || window.navigator.maxTouchPoints > 0 || window.navigator.msMaxTouchPoints > 0
}

export const trigger = (eventId, obj = {}, scope = document, doBubble = false) => {
  if (window.location.hostname === "localhost") {
    // eslint-disable-next-line no-console
    console.log(eventId, obj)
  }
  const event = new CustomEvent(eventId, {
    detail: obj,
    bubbles: doBubble,
  })
  scope.dispatchEvent(event)
}

export const click = () => {
  return isTouchDevice() ? "touchstart" : "click"
}

export const getURLParams = () => {
  const s = window.location.search.split("+").join(encodeURIComponent("+"))
  return Object.fromEntries(new URLSearchParams(s))
}

// parses an advanced search term (e.g. "location:Budapest") into { key, value }, returns null for any other search value
export const parseAdvancedSearchTerm = term => {
  const [key, value] = term.split(":").map(s => s.trim())
  return value !== undefined && config().ADVANCED_SEARCH_KEYS.includes(key) ? { key, value } : null
}

// converts search input values into query params (advanced search terms become their own params, the rest goes to q)
export const searchValuesToQueryParams = values => {
  const params = []
  const queryValues = []

  values.forEach(value => {
    const term = parseAdvancedSearchTerm(value)
    if (term) {
      params.push(`${term.key}=${term.value}`)
    } else {
      queryValues.push(value)
    }
  })

  if (params.length > 0) params.push(`advancedSearch=1`)
  if (queryValues.length > 0) params.push(`q=${queryValues.join(", ")}`)

  return params
}

// returns the advanced search term keys of the url params (also set without the advancedSearch flag by sidebar links,
// e.g. ?donor=...), except "id" which only opens a photo unless it's part of an advanced search
export const getSearchTermKeys = params =>
  Object.keys(params).filter(
    key => config().ADVANCED_SEARCH_KEYS.includes(key) && (key !== "id" || params.advancedSearch)
  )

// converts url params into search input values (the reverse of searchValuesToQueryParams)
export const urlParamsToSearchValues = params => {
  const termKeys = getSearchTermKeys(params)

  return Object.keys(params)
    .filter(key => key === "q" || termKeys.includes(key))
    .map(key => (key === "q" ? `${params[key]}` : `${key}:${params[key]}`))
}

export const urlToArray = path => {
  const valuesArray = (path || window.location.pathname).split("/")
  while (valuesArray[0] === "") valuesArray.shift()
  while (valuesArray[valuesArray.length - 1] === "") valuesArray.pop()
  return valuesArray
}

export const removeClassByPrefix = (el, prefix) => {
  const regx = new RegExp(`\\b${prefix}(.[^\\s]*)?\\b`, "g")
  // eslint-disable-next-line no-param-reassign
  el.className = el.className.replace(regx, "")
  return el
}

export const numberWithCommas = x => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const slugify = (str, removeSpaces) => {
  let s = str.toString()

  const map = {
    a: "Ä|ä|À|à|Á|á|Â|â|Ã|ã|Å|å|Ǎ|ǎ|Ą|ą|Ă|ă|Æ|æ|Ā|ā",
    c: "Ç|ç|Ć|ć|Ĉ|ĉ|Č|č",
    d: "Ď|đ|Đ|ď|ð",
    e: "È|è|É|é|Ê|ê|Ë|ë|Ě|ě|Ę|ę|Ė|ė|Ē|ē",
    g: "Ĝ|ĝ|Ģ|ģ|Ğ|ğ",
    h: "Ĥ|ĥ",
    i: "Ì|ì|Í|í|Î|î|Ï|ï|ı|Ī|ī|Į|į",
    j: "Ĵ|ĵ",
    k: "Ķ|ķ",
    l: "Ĺ|ĺ|Ļ|ļ|Ł|ł|Ľ|ľ|Ŀ|ŀ",
    n: "Ñ|ñ|Ń|ń|Ň|ň|Ņ|ņ",
    o: "Ö|ö|Ò|ò|Ó|ó|Ô|ô|Õ|õ|Ő|ő",
    r: "Ŕ|ŕ|Ř|ř",
    s: "Ś|ś|Ŝ|ŝ|Ş|ş|Š|š|Ș|ș",
    t: "Ť|ť|Ţ|ţ|Þ|þ|Ț|ț",
    u: "Ü|ü|Ù|ù|Ú|ú|Û|û|Ű|ű|Ũ|ũ|Ų|ų|Ů|ů|Ū|ū",
    w: "Ŵ|ŵ",
    y: "Ý|ý|Ÿ|ÿ|Ŷ|ŷ",
    z: "Ź|ź|Ž|ž|Ż|ż",
  }

  s = s.toLowerCase()

  Object.keys(map).forEach(pattern => {
    s = s.replace(new RegExp(map[pattern], "g"), pattern)
  })

  if (removeSpaces) {
    s = s.replace(new RegExp("·|/|_|,|:|;| ", "g"), "-")
    s = s.replace(new RegExp("\\?|\\=|\\&|\\<|\\>", "g"), "")
  }

  return s
}

export const setPageMeta = (title, description, imgSrc) => {
  if (title) {
    const titleText = `Fortepan — ${title}`
    document.title = titleText
    document.querySelector('meta[property="twitter:title"]').setAttribute("content", titleText)
    document.querySelector('meta[property="og:title"]').setAttribute("content", titleText)
  }
  if (description) {
    document.querySelector('meta[name="description"]').setAttribute("content", description)
    document.querySelector('meta[property="twitter:description"]').setAttribute("content", description)
    document.querySelector('meta[property="og:description"]').setAttribute("content", description)
  }
  if (imgSrc) {
    document.querySelector('meta[property="twitter:image:src"]').setAttribute("content", imgSrc)
    document.querySelector('meta[property="og:image"]').setAttribute("content", imgSrc)
  }
}

export const getImgAltText = data => {
  return [
    data.country,
    data.place,
    data.city,
    data.description,
    data.year,
    data.donor,
    data.author,
    (data.tags || []).join(", "),
    data.mid ? `Fortepan #${data.mid}` : false,
  ]
    .filter(Boolean)
    .join(", ")
}

export const onClassChange = (node, callback) => {
  const classObserver = new window.MutationObserver(() => {
    callback(node)
  })
  classObserver.observe(node, {
    attributes: true,
    attributeFilter: ["class"],
  })
}

export const copyToClipboard = (textToCopy, type, successMessage, errorMessage) => {
  const input = document.createElement("textarea")
  input.className = "visuallyhidden"
  input.value = textToCopy

  document.body.appendChild(input)

  input.select()

  const res = document.execCommand("copy")
  if (res) {
    trigger("snackbar:show", {
      message: successMessage || (type === "link" ? lang("copy_link_clipboard") : lang("copy_text_clipboard")),
      autoHide: true,
      status: "success",
    })
  } else {
    trigger("snackbar:show", {
      message:
        errorMessage || (type === "link" ? lang("copy_link_clipboard_failed") : lang("copy_text_clipboard_failed")),
      autoHide: true,
      status: "error",
    })
  }

  document.body.removeChild(input)
  return res
}

export const isElementInViewport = (el, complete = true) => {
  if (el) {
    const top = document.querySelector(".header-nav") ? document.querySelector(".header-nav").offsetHeight : 0
    const bounds = el.getBoundingClientRect()

    if (!complete) {
      // check for just the top coordinate
      return bounds.top >= top && bounds.top <= window.innerHeight
    }

    return bounds.top >= top && bounds.bottom <= window.innerHeight
  }
  return false
}

export const setCookie = (name, value, days) => {
  let expires = ""
  if (days) {
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = `; expires=${date.toUTCString()}`
  }
  document.cookie = `${name}=${value || ""}${expires}; path=/`
}

export const getCookie = name => {
  const nameEQ = `${name}=`
  const ca = document.cookie.split(";")
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i]
    while (c.charAt(0) === " ") c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

export const eraseCookie = name => {
  document.cookie = `${name}=; Max-Age=-99999999;`
}

export const validateEmail = email => {
  const reg = /^([A-Za-z0-9_\-.])+@([A-Za-z0-9_\-.])+.([A-Za-z]{2,4})$/
  return reg.test(email)
}

const globalSettingsStorage = {}
export const globalSettings = {
  setItem: (key, value) => {
    globalSettingsStorage[key] = value
  },
  getItem: key => {
    return globalSettingsStorage[key]
  },
  removeItem: key => {
    delete globalSettingsStorage[key]
  },
}

export const asArray = value => {
  if (value == null || value === "") return []
  return Array.isArray(value) ? value.filter(v => v != null && v !== "") : [value]
}

export const escapeHTML = unsafe => {
  if (unsafe == null) return ""
  return String(unsafe).replace(/[\u00A0-\u9999<>&]/g, i => {
    return `&#${i.charCodeAt(0)};`
  })
}

export const formatNumber = number => {
  const options = {
    useGrouping: true,
    maximumFractionDigits: 2,
  }

  if (getLocale() === "en") {
    options.notation = "compact"
    options.compactDisplay = "short"
  }

  return new Intl.NumberFormat(getLocale(), options).format(number)
}
